import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { connectSocket } from '../socket';

const CELL = 30;
const SIZE = 15 * CELL;
const COLOR = { red: '#e24b4a', green: '#1d9e75', yellow: '#e0a012', blue: '#2563b8' };
const COLOR_LABEL = { red: 'Đỏ', green: 'Lục', yellow: 'Vàng', blue: 'Lam' };

// Vòng ngoài 52 ô (col,row) theo bàn ludo 15x15, theo chiều kim đồng hồ.
const PATH = [
  [1, 6], [2, 6], [3, 6], [4, 6], [5, 6], [6, 5], [6, 4], [6, 3], [6, 2], [6, 1], [6, 0], [7, 0],
  [8, 0], [8, 1], [8, 2], [8, 3], [8, 4], [8, 5], [9, 6], [10, 6], [11, 6], [12, 6], [13, 6], [14, 6], [14, 7],
  [14, 8], [13, 8], [12, 8], [11, 8], [10, 8], [9, 8], [8, 9], [8, 10], [8, 11], [8, 12], [8, 13], [8, 14], [7, 14],
  [6, 14], [6, 13], [6, 12], [6, 11], [6, 10], [6, 9], [5, 8], [4, 8], [3, 8], [2, 8], [1, 8], [0, 8], [0, 7], [0, 6],
];
// Xếp màu: trên-trái ĐỎ, trên-phải LAM, dưới-phải VÀNG, dưới-trái LỤC.
// LOGIC BÀN THẬT: quân chạy NGƯỢC chiều kim đồng hồ (PATH đánh index theo chiều kim
// đồng hồ nên đổi rel -> ô tuyệt đối là phép TRỪ). Ô XUẤT QUÂN nằm đầu nhánh sát chuồng
// xuất phát nhưng KHÔNG thuộc đường đua (quân khác chạy ngang bỏ qua ô này);
// CỬA CHUỒNG (ô mũi tên) là ô GIỮA CẠNH bàn ngay sau đó theo chiều chạy
// -> quân đi đúng 48 bước là tới cửa rồi leo lần lượt ô số 1..6 vào tâm bàn.
// START_INDEX phải khớp y hệt backend (game/rules.js) — lệch là quân vẽ sai ô.
const START_INDEX = { red: 10, blue: 23, yellow: 36, green: 49 };
const HOME = {
  red: [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5], [7, 6]],        // trên-trái -> chuồng nhánh TRÊN
  blue: [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7], [8, 7]],   // trên-phải -> nhánh PHẢI
  yellow: [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9], [7, 8]], // dưới-phải -> nhánh DƯỚI
  green: [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7], [6, 7]],      // dưới-trái -> nhánh TRÁI
};
const YARD = {
  red: [[1, 1], [3, 1], [1, 3], [3, 3]],
  blue: [[10, 1], [12, 1], [10, 3], [12, 3]],
  yellow: [[10, 10], [12, 10], [10, 12], [12, 12]],
  green: [[1, 10], [3, 10], [1, 12], [3, 12]],
};
const YARD_BOX = { red: [0, 0], blue: [9, 0], yellow: [9, 9], green: [0, 9] };

// Góc quay đưa chuồng của từng màu ra PHÍA TRƯỚC (đáy màn hình).
// Tâm bàn ở ô (7.5,7.5); chuồng mỗi màu ở 1 góc -> tính góc rồi xoay cho về hướng 90° (đáy).
const FACE_ROT = { red: -135, blue: 135, yellow: 45, green: -45 };

const center = ([c, r]) => ({ x: (c + 0.5) * CELL, y: (r + 0.5) * CELL });
// Vị trí quân theo mô hình luật (khớp backend game/rules.js): -1 = trong sân;
// 0 = Ô XUẤT QUÂN (ngoài đường đua); 1..48 = đường đua (48 = CỬA CHUỒNG giữa cạnh);
// 49..54 = đã lên chuồng, ô số 1..6. Đường đua BỎ QUA 4 ô xuất quân trên vòng vẽ:
// đi ngược kim đồng hồ (trừ dần index), cứ 12 bước lại nhảy qua thêm 1 ô xuất quân.
const GATE_REL = 48;
function trackIndex(color, pos) {
  if (pos === 0) return START_INDEX[color];
  return (((START_INDEX[color] - pos - Math.floor((pos - 1) / 12)) % 52) + 52) % 52;
}
function pieceCenter(color, pos, idx) {
  if (pos === -1) return center(YARD[color][idx]);
  if (pos <= GATE_REL) return center(PATH[trackIndex(color, pos)]);
  return center(HOME[color][pos - GATE_REL - 1]);
}

export default function CaNguaPage() {
  const { me } = useAuth();
  const myId = String(me.id || me._id);
  const [rooms, setRooms] = useState([]);
  const [room, setRoom] = useState(null);
  const [joinId, setJoinId] = useState('');
  const [err, setErr] = useState('');
  const sockRef = useRef(null);

  useEffect(() => {
    const socket = connectSocket();
    sockRef.current = socket;
    if (!socket) return;
    const onState = (r) => setRoom(r);
    const onRooms = (list) => setRooms(list || []);
    socket.on('cangua:state', onState);
    socket.on('cangua:rooms', onRooms);
    socket.emit('cangua:rooms', (list) => setRooms(list || []));
    return () => {
      socket.off('cangua:state', onState);
      socket.off('cangua:rooms', onRooms);
    };
  }, []);

  // Truyền đúng số tham số (không kèm undefined ở cuối) để socket.io nhận ack.
  const send = (...args) => sockRef.current?.emit(...args);

  function create() {
    send('cangua:create', (res) => res?.ok && setRoom(res.room));
  }
  function join(id) {
    setErr('');
    send('cangua:join', { roomId: id }, (res) => {
      if (res?.ok) setRoom(res.room);
      else setErr(res?.error || 'Không vào được phòng');
    });
  }
  function leave() {
    if (room) send('cangua:leave', { roomId: room.id });
    setRoom(null);
  }
  const start = () => send('cangua:start', { roomId: room.id });
  const roll = () => send('cangua:roll', { roomId: room.id });
  const move = (moveIndex) => send('cangua:move', { roomId: room.id, moveIndex });
  const setMode = (mode) => send('cangua:mode', { roomId: room.id, mode });

  // ===== Sảnh =====
  if (!room) {
    return (
      <>
        <div className="page-head">
          <div>
            <h2>🎲 Cá Ngựa online</h2>
            <div className="sub">Tạo phòng rồi rủ đồng nghiệp cùng chơi</div>
          </div>
        </div>
        <div className="card">
          <div className="row" style={{ gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
            <button className="btn" onClick={create}>+ Tạo phòng mới</button>
            <div className="row" style={{ gap: 6 }}>
              <input
                style={{ width: 140 }}
                placeholder="Mã phòng…"
                value={joinId}
                onChange={(e) => setJoinId(e.target.value.toUpperCase())}
              />
              <button className="btn ghost" disabled={!joinId.trim()} onClick={() => join(joinId.trim())}>
                Vào phòng
              </button>
            </div>
          </div>
          {err && <div className="small" style={{ color: 'var(--danger)', marginBottom: 10 }}>{err}</div>}
          <h3 style={{ fontSize: 14, marginBottom: 8 }}>Phòng đang mở ({rooms.length})</h3>
          {!rooms.length ? (
            <div className="empty">Chưa có phòng nào. Tạo phòng mới để bắt đầu!</div>
          ) : (
            <div className="tbl-wrap">
            <table>
              <thead>
                <tr><th>Mã phòng</th><th>Chủ phòng</th><th className="c">Số quân</th><th className="c">Người chơi</th><th></th></tr>
              </thead>
              <tbody>
                {rooms.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 700 }}>{r.id}</td>
                    <td>{r.hostName}</td>
                    <td className="c">{r.mode || 4}</td>
                    <td className="c">{r.count}/4</td>
                    <td className="r">
                      <button className="btn sm" disabled={r.count >= 4} onClick={() => join(r.id)}>Vào</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </>
    );
  }

  // ===== Trong phòng =====
  const meIdx = room.players.findIndex((p) => p.userId === myId);
  const isHost = room.hostId === myId;
  const curPlayer = room.started ? room.players[room.turnIndex] : null;
  const myTurn = curPlayer && curPlayer.userId === myId && !room.over;
  const canRoll = myTurn && !room.awaitingMove && room.dice == null;
  const winner = room.winner ? room.players.find((p) => p.userId === room.winner) : null;
  const nPieces = room.mode || 4;

  return (
    <>
      <div className="page-head">
        <div>
          <h2>🎲 Phòng <span style={{ fontFamily: 'var(--font-mono)' }}>{room.id}</span></h2>
          <div className="sub">{room.started ? 'Đang chơi' : 'Đang chờ người chơi'}</div>
        </div>
        <button className="btn ghost sm" onClick={leave}>← Rời phòng</button>
      </div>

      <div className="cangua-wrap">
        <div className="card cangua-board-card">
          <Board3D room={room} myId={myId} myTurn={myTurn} onMove={move} />
        </div>

        <div className="card cangua-side">
          {winner && (
            <div className="cangua-winner">🏆 <b>{winner.name}</b> ({COLOR_LABEL[winner.color]}) về NHẤT!</div>
          )}
          {/* Xếp hạng: có người về nhất thì những người còn lại VẪN CHƠI TIẾP tới khi hết. */}
          {(room.ranks || []).length > 0 && (
            <div className="small" style={{ marginBottom: 8 }}>
              {room.ranks.map((x) => (
                <div key={x.userId}>{['🥇', '🥈', '🥉'][x.rank - 1] || `#${x.rank}`} {x.name}</div>
              ))}
              {!room.over && <div className="muted" style={{ marginTop: 4 }}>Những người còn lại chơi tiếp…</div>}
            </div>
          )}
          {room.over ? (
            <div className="cangua-status">Ván kết thúc — tất cả đã lên chuồng 🎉</div>
          ) : room.started ? (
            <div className="cangua-status">
              {myTurn ? (
                room.awaitingMove ? (
                  <div>Tới lượt bạn — <b>bấm ô đích nhấp nháy</b> để đi{room.dice === 1 ? ' (ra 1: đi 1 ô hoặc nhảy về cửa chuồng)' : ''}</div>
                ) : (
                  <div>Tới lượt bạn — hãy lắc xúc xắc{room.rolledSix ? ' (ra 6 — được xúc tiếp!)' : ''}</div>
                )
              ) : (
                <div>Đến lượt <b style={{ color: COLOR[curPlayer?.color] }}>{curPlayer?.name}</b>…</div>
              )}
              {room.lastKick && <div className="small" style={{ color: 'var(--danger)', marginTop: 4 }}>💥 {room.lastKick.name} bị đá về chuồng!</div>}
            </div>
          ) : (
            <div className="cangua-status">Chờ chủ phòng bắt đầu (cần ≥ 2 người)</div>
          )}

          <div className="cangua-dice-row">
            <Dice value={room.dice} />
            {room.started && !room.over && (
              <button className="btn" disabled={!canRoll} onClick={roll}>
                {room.awaitingMove && myTurn ? 'Chọn ô đích…' : 'Lắc xúc xắc'}
              </button>
            )}
          </div>

          {/* Chế độ 3 hoặc 4 quân — chủ phòng chọn trước khi bắt đầu. */}
          {!room.started && (
            <div className="row" style={{ gap: 6, marginTop: 12, alignItems: 'center' }}>
              <span className="small muted">Số quân:</span>
              {[3, 4].map((m) => (
                <button key={m} className={'btn sm' + (nPieces === m ? '' : ' ghost')} disabled={!isHost} onClick={() => setMode(m)}>{m} quân</button>
              ))}
            </div>
          )}

          <h3 style={{ fontSize: 13, margin: '14px 0 6px' }}>Người chơi ({room.players.length}/4)</h3>
          <div className="cangua-players">
            {room.players.map((p, i) => (
              <div key={p.userId} className={'cangua-player' + (room.started && i === room.turnIndex ? ' turn' : '')}>
                <span className="cangua-dot" style={{ background: COLOR[p.color] }} />
                <span className="cangua-pname">{p.name}{p.userId === myId ? ' (bạn)' : ''}</span>
                <span className="small muted">{p.pieces.filter((x) => x > GATE_REL).length}/{nPieces} lên chuồng</span>
                {p.finished && <span className="small" style={{ color: 'var(--ok)' }}>{['🥇', '🥈', '🥉'][p.rank - 1] || `#${p.rank}`}</span>}
                {!p.connected && <span className="small" style={{ color: 'var(--danger)' }}>mất KN</span>}
              </div>
            ))}
          </div>

          {!room.started && isHost && (
            <button className="btn full" style={{ marginTop: 14 }} disabled={room.players.length < 2} onClick={start}>
              ▶ Bắt đầu ván
            </button>
          )}
          {!room.started && (
            <div className="small muted" style={{ marginTop: 10 }}>
              Chia sẻ mã phòng <b style={{ fontFamily: 'var(--font-mono)' }}>{room.id}</b> cho đồng nghiệp để cùng vào.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Xúc xắc hiển thị bằng số chấm.
function Dice({ value }) {
  const pips = {
    1: [4], 2: [0, 8], 3: [0, 4, 8], 4: [0, 2, 6, 8], 5: [0, 2, 4, 6, 8], 6: [0, 2, 3, 5, 6, 8],
  };
  return (
    <div className="cangua-dice">
      {value ? (
        Array.from({ length: 9 }).map((_, i) => (
          <span key={i} className="cangua-pip" style={{ visibility: (pips[value] || []).includes(i) ? 'visible' : 'hidden' }} />
        ))
      ) : (
        <span className="cangua-dice-empty">🎲</span>
      )}
    </div>
  );
}

const KIND_LABEL = {
  deploy: 'Xuất quân', move: 'Đi', jump: 'Nhảy về cửa chuồng',
  kickBack: 'Đá lùi', enterHome: 'Vào chuồng', homeStep: 'Tiến trong chuồng',
};

// Bàn 3D: dùng CSS 3D (perspective + rotateX/rotateZ) — KHÔNG cần WebGL nên không treo
// trên máy tắt tăng tốc phần cứng. Kéo chuột/chạm để xoay quanh trục đứng + đổi độ nghiêng.
function Board3D({ room, myId, myTurn, onMove }) {
  const meColor = room.players.find((p) => p.userId === myId)?.color;
  const [rot, setRot] = useState(() => (meColor ? FACE_ROT[meColor] : 0));
  const [tilt, setTilt] = useState(52);
  const [grab, setGrab] = useState(false);
  const drag = useRef(null);
  const faced = useRef(false);

  // Lần đầu biết màu của mình -> tự xoay chuồng mình ra phía trước.
  useEffect(() => {
    if (meColor && !faced.current) { faced.current = true; setRot(FACE_ROT[meColor]); }
  }, [meColor]);

  const onDown = (e) => {
    if (e.button != null && e.button !== 0) return; // chỉ chuột trái
    if (e.cancelable) e.preventDefault(); // chặn bôi đen chữ khi kéo
    const pt = e.touches?.[0] || e;
    drag.current = { x: pt.clientX, y: pt.clientY, rot, tilt };
    setGrab(true);
  };
  const onMoveDrag = (e) => {
    if (!drag.current) return;
    const pt = e.touches?.[0] || e;
    const d = drag.current;
    setRot(d.rot + (pt.clientX - d.x) * 0.5); // kéo ngang -> xoay
    setTilt(Math.min(75, Math.max(0, d.tilt + (pt.clientY - d.y) * 0.3))); // kéo dọc -> nghiêng
  };
  const onUp = () => { drag.current = null; setGrab(false); };

  useEffect(() => {
    if (!grab) return;
    window.addEventListener('mousemove', onMoveDrag);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMoveDrag, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMoveDrag);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMoveDrag);
      window.removeEventListener('touchend', onUp);
    };
  });

  return (
    <div className="cangua-3d">
      <div className={'cangua-stage' + (grab ? ' grabbing' : '')} onMouseDown={onDown} onTouchStart={onDown}>
        <div className="cangua-plane" style={{ transform: `rotateX(${tilt}deg) rotateZ(${rot}deg)` }}>
          <Board room={room} myId={myId} myTurn={myTurn} onMove={onMove} rot={rot} tilt={tilt} />
        </div>
      </div>
      <div className="cangua-3d-ctl">
        <button className="btn sm ghost" title="Xoay trái" onClick={() => setRot((v) => v - 45)}>⟲</button>
        <button className="btn sm ghost" title="Xoay phải" onClick={() => setRot((v) => v + 45)}>⟳</button>
        <input type="range" min="0" max="75" value={tilt} title="Độ nghiêng"
          onChange={(e) => setTilt(Number(e.target.value))} style={{ width: 90 }} />
        {meColor && <button className="btn sm ghost" onClick={() => setRot(FACE_ROT[meColor])}>Về phía tôi</button>}
        <button className="btn sm ghost" title="Nhìn thẳng từ trên" onClick={() => { setTilt(0); setRot(meColor ? FACE_ROT[meColor] : 0); }}>Đặt lại</button>
      </div>
      <div className="small muted" style={{ textAlign: 'center', marginTop: 4 }}>
        Kéo bàn để xoay · kéo lên/xuống để đổi độ nghiêng
        {meColor && <> · Bạn là <b style={{ color: COLOR[meColor] }}>{COLOR_LABEL[meColor]}</b></>}
      </div>
    </div>
  );
}

// Lệch nhẹ khi nhiều quân đứng chung 1 ô (để không che nhau hoàn toàn).
function pieceOffset(pos, idx) {
  if (pos < 0) return { dx: 0, dy: 0 }; // trong sân: đã có ô riêng
  return { dx: (idx % 2) * 6 - 3, dy: (idx > 1 ? 1 : -1) * 2 };
}

// Con ngựa nhìn nghiêng — dáng QUÂN MÃ cờ vua (đầu + bờm + cổ), đứng DỰNG nhờ billboard.
function Horse({ color, dim }) {
  const c = COLOR[color];
  return (
    <svg className="cangua-horse" viewBox="0 0 45 50" style={{ opacity: dim ? 0.5 : 1 }}>
      {/* đế */}
      <ellipse cx="22.5" cy="44.5" rx="14.5" ry="3.4" fill={c} stroke="#fff" strokeWidth="1.6" />
      <g fill={c} stroke="#fff" strokeWidth="1.7" strokeLinejoin="round" strokeLinecap="round">
        {/* thân/cổ */}
        <path d="M22 10 C32.5 11 38.5 18 38 41 L15 41 C15 31 25 33.5 23 18" />
        {/* đầu + mõm + tai */}
        <path d="M24 18 C24.4 20.9 18.5 25.4 16 27 C13 29 13.2 31.3 11 31 C10 30.1 12.4 28 11 28 C10 28 11.2 29.2 10 30 C9 30 6 31 6 26 C6 24 12 14 12 14 C12 14 13.9 12.1 14 10.5 C13.3 9.5 13.5 8.5 13.5 7.5 C14.5 6.5 16.5 10 16.5 10 L18.5 10 C18.5 10 19.3 8 21 7 C22 7 22 10 22 10" />
      </g>
      {/* mắt + lỗ mũi */}
      <circle cx="10.2" cy="25.6" r="1.7" fill="#fff" />
      <circle cx="7.4" cy="29.4" r="0.9" fill="#fff" opacity=".8" />
      {/* bờm */}
      <path d="M24.5 15 C28 17 30.5 20.5 31.5 25" fill="none" stroke="#fff" strokeWidth="1.4" opacity=".55" strokeLinecap="round" />
    </svg>
  );
}

function Board({ room, myId, myTurn, onMove, rot = 0, tilt = 0 }) {
  const legal = myTurn && room.awaitingMove ? room.legalMoves || [] : [];
  const meColor = room.players.find((p) => p.userId === myId)?.color;
  const movablePieces = new Set(legal.map((m) => m.piece));
  const pct = (v) => (v / SIZE) * 100 + '%';

  return (
    <div className="cangua-boardwrap">
    <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="cangua-svg" role="img" aria-label="Bàn cờ cá ngựa">
      {/* Nền */}
      <rect x="0" y="0" width={SIZE} height={SIZE} rx="10" fill="var(--panel2)" />

      {/* Chuồng 4 góc */}
      {Object.entries(YARD_BOX).map(([color, [c, r]]) => (
        <g key={color}>
          <rect x={c * CELL + 3} y={r * CELL + 3} width={6 * CELL - 6} height={6 * CELL - 6} rx="12" fill={COLOR[color]} opacity="0.16" stroke={COLOR[color]} strokeWidth="2" />
          {YARD[color].map((cell, i) => {
            const p = center(cell);
            return <circle key={i} cx={p.x} cy={p.y} r="9" fill="none" stroke={COLOR[color]} strokeWidth="2" opacity="0.5" />;
          })}
        </g>
      ))}

      {/* Ô vòng ngoài — tô ô XUẤT QUÂN (đậm) và ô CỬA CHUỒNG (nhạt, có mũi tên) của từng màu */}
      {PATH.map(([c, r], i) => {
        const startColor = Object.keys(START_INDEX).find((k) => START_INDEX[k] === i);
        const doorColor = Object.keys(START_INDEX).find((k) => (START_INDEX[k] + 1) % 52 === i);
        const owner = startColor || doorColor;
        return (
          <rect key={i} x={c * CELL + 1.5} y={r * CELL + 1.5} width={CELL - 3} height={CELL - 3} rx="5"
            fill={owner ? COLOR[owner] : 'var(--panel)'} opacity={startColor ? 0.85 : doorColor ? 0.55 : 1}
            stroke="var(--border)" strokeWidth="1" />
        );
      })}

      {/* Mũi tên ở ô cửa chuồng — chỉ hướng leo vào chuồng (như bàn thật) */}
      {Object.keys(START_INDEX).map((color) => {
        const p = center(PATH[(START_INDEX[color] + 1) % 52]);
        const h = center(HOME[color][0]);
        const ang = (Math.atan2(h.y - p.y, h.x - p.x) * 180) / Math.PI;
        return (
          <polygon key={'door' + color} points="-4.5,-5.5 6.5,0 -4.5,5.5" fill="#fff" opacity="0.9"
            pointerEvents="none" transform={`translate(${p.x} ${p.y}) rotate(${ang})`} />
        );
      })}

      {/* Đường về đích (6 ô mỗi màu) — đánh số 1..6 từ NGOÀI vào TRONG.
          Số xoay ngược -rot quanh tâm ô nên luôn đứng thẳng hướng người xem dù bàn xoay. */}
      {Object.entries(HOME).map(([color, cells]) =>
        cells.map(([c, r], i) => (
          <g key={color + i}>
            <rect x={c * CELL + 1.5} y={r * CELL + 1.5} width={CELL - 3} height={CELL - 3} rx="5"
              fill={COLOR[color]} opacity={i === 5 ? 0.95 : 0.4} stroke="var(--border)" strokeWidth="1" />
            <text x={(c + 0.5) * CELL} y={(r + 0.5) * CELL} textAnchor="middle" dominantBaseline="central"
              fontSize="12" fontWeight="700" fill="#fff" opacity="0.85" pointerEvents="none"
              transform={`rotate(${-rot} ${(c + 0.5) * CELL} ${(r + 0.5) * CELL})`}>{i + 1}</text>
          </g>
        ))
      )}

      {/* BÓNG ĐỔ của quân — nằm PHẲNG trên mặt bàn (con ngựa đứng dựng là lớp phủ riêng bên trên) */}
      {room.players.map((p) =>
        p.pieces.map((pos, idx) => {
          const { x, y } = pieceCenter(p.color, pos, idx);
          const isMine = p.userId === myId;
          const movable = isMine && movablePieces.has(idx);
          const { dx, dy } = pieceOffset(pos, idx);
          return (
            <g key={p.color + idx}>
              {movable && <circle cx={x + dx} cy={y + dy} r="12" fill="none" stroke={COLOR[p.color]} strokeWidth="2.5" className="cangua-legal" />}
              <ellipse cx={x + dx} cy={y + dy + 1} rx="8" ry="5" fill="#000" opacity="0.28" />
            </g>
          );
        })
      )}

      {/* Ô ĐÍCH bấm được — 1 quân có thể có nhiều lựa chọn (ra 1: đi 1 ô hay nhảy về cửa;
          hoặc vừa đi tiến vừa đá lùi), nên chọn theo ĐÍCH chứ không chọn theo quân. */}
      {legal.map((mv, i) => {
        const { x, y } = pieceCenter(meColor, mv.to, mv.piece);
        return (
          <g key={'mv' + i} style={{ cursor: 'pointer' }} onClick={() => onMove(i)} className="cangua-target">
            <title>{KIND_LABEL[mv.kind] || 'Đi'}{mv.kick ? ' + ĐÁ quân địch' : ''}</title>
            <circle cx={x} cy={y} r="12" fill={mv.kick ? 'var(--danger)' : COLOR[meColor]} opacity={mv.kick ? 0.5 : 0.3} stroke={mv.kick ? 'var(--danger)' : COLOR[meColor]} strokeWidth="2" strokeDasharray="3 2" />
            {mv.kick && <text x={x} y={y + 4} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">×</text>}
          </g>
        );
      })}
    </svg>

    {/* LỚP PHỦ QUÂN NGỰA — mỗi con là 1 mốc 0x0 đặt đúng ô, xoay NGƯỢC lại đúng góc bàn
        (rotateZ(-rot) rotateX(-tilt)) nên luôn ĐỨNG DỰNG hướng về người xem dù bàn xoay kiểu gì. */}
    <div className="cangua-pieces">
      {room.players.map((p) =>
        p.pieces.map((pos, idx) => {
          const { x, y } = pieceCenter(p.color, pos, idx);
          const { dx, dy } = pieceOffset(pos, idx);
          const movable = p.userId === myId && movablePieces.has(idx);
          return (
            <div
              key={p.color + idx}
              className={'cangua-anchor' + (movable ? ' movable' : '')}
              style={{ left: pct(x + dx), top: pct(y + dy), transform: `rotateZ(${-rot}deg) rotateX(${-tilt}deg)` }}
            >
              <Horse color={p.color} dim={pos > GATE_REL} />
            </div>
          );
        })
      )}
    </div>
    </div>
  );
}
